import { useTranslation } from 'react-i18next';
import { Maximize2, X, Youtube } from 'lucide-react';
import clsx from 'clsx';
import { TeamLiveStrap } from './TeamLiveStrap';
import type { TeamLiveTileDrag } from './TeamLiveSortableGrid';
import type { TeamExternalStream } from '@/utils/teamLiveExternalStreams';

export interface ExternalStreamTileProps {
  stream: TeamExternalStream;
  onRemove: () => void;
  /** Drag affordance from the sortable grid; null when reordering is off. */
  drag?: TeamLiveTileDrag | null;
  /** Mobile: opens the focus viewer (iframe taps can't reach us). */
  onExpand?: () => void;
  /** The same stream is open in the focus viewer; drop the duplicate iframe. */
  paused?: boolean;
  /** When false, the iframe ignores pointer events (focus viewer: lets swipe through). */
  interactive?: boolean;
}

/**
 * A viewer-pinned external stream (YouTube live) on the Team Live multiview:
 * a muted-autoplay nocookie embed with YouTube's native controls, and the same
 * bottom strap the member tiles use — label, remove control, and the drag
 * surface for reordering. No REC control, status badges, or polling — see
 * docs/superpowers/specs/2026-08-23-team-live-external-streams-design.md
 *
 * The strap is why reordering works here at all: the iframe swallows every
 * pointer event inside it, so only an overlay of our own can start a drag
 * (issue #738).
 */
export function ExternalStreamTile({
  stream,
  onRemove,
  drag,
  onExpand,
  paused,
  interactive = true,
}: ExternalStreamTileProps) {
  const { t } = useTranslation('pages');
  const title = stream.label ?? t('teamLive.externalStream');

  return (
    <div
      data-testid="external-stream-tile"
      className="relative min-h-0 overflow-hidden rounded-lg border border-white/10 bg-secondary-900"
    >
      {paused ? (
        <div className="flex h-full flex-col items-center justify-center gap-2 text-white/50">
          <Youtube className="h-6 w-6" />
          <span className="text-xs">
            {t('teamLive.tileStatus.live', {
              defaultValue: t('sessions.detail.liveStream.liveBadge'),
            })}
          </span>
        </div>
      ) : (
        <iframe
          data-testid="external-stream-iframe"
          src={`https://www.youtube-nocookie.com/embed/${stream.videoId}?autoplay=1&mute=1`}
          title={title}
          className={clsx('h-full w-full', !interactive && 'pointer-events-none')}
          allow="autoplay; encrypted-media; picture-in-picture"
          allowFullScreen
        />
      )}

      <div
        {...drag?.strapProps}
        className="absolute inset-x-0 bottom-0 flex items-center gap-2 bg-gradient-to-t from-black/75 via-black/45 to-transparent px-2 py-1.5"
      >
        <TeamLiveStrap
          title={title}
          // Only when a label exists — otherwise the title already says it.
          subtitle={stream.label ? t('teamLive.externalStream') : null}
          accentClassName="bg-sky-400"
          drag={drag}
        >
          {onExpand ? (
            <button
              type="button"
              onClick={onExpand}
              onPointerDown={(event) => event.stopPropagation()}
              aria-label={t('teamLive.expandTile', { title })}
              className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-white/20 bg-black/50 text-white/80 backdrop-blur-sm transition-colors hover:bg-black/70"
            >
              <Maximize2 className="h-3.5 w-3.5" />
            </button>
          ) : null}
          <button
            type="button"
            onClick={onRemove}
            onPointerDown={(event) => event.stopPropagation()}
            aria-label={t('teamLive.removeExternalStream')}
            className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-white/20 bg-black/50 text-white/80 backdrop-blur-sm transition-colors hover:bg-black/70"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </TeamLiveStrap>
      </div>
    </div>
  );
}
